import {
  AnchorType,
  CheckStatus,
  EntityRefUtil,
  formatDateWithUserTimezone,
  GetChecksRequestDTO,
  GetChecksResponseDTO,
  GetUnitsResponseDTO,
  isCheckCompleted,
  RecordState,
  TagEntity,
} from '@gearbelt/global';
import { TooltipArrow } from '@radix-ui/react-tooltip';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import CheckIcon from '@/components/icons/check';
import InformationIcon from '@/components/icons/information';
import { ASSET_SECTION_LABEL } from '@/components/table-page/columns/asset-tags-column';
import { createTagsColumn } from '@/components/table-page/columns/tags-column';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { formatScheduleCheckLabel } from '@/utils/schedules';

const columnHelper = createColumnHelper<GetChecksResponseDTO['data'][number]>();

export const createAssetScheduleCheckColumns = (opts?: {
  units?: GetUnitsResponseDTO['data'];
}): AccessorKeyColumnDef<GetChecksResponseDTO['data'][number]>[] => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    // 1. Select checkbox
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    // 2. Status
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue();

        const badgeClasses = match(status)
          .with(CheckStatus.Open, () => 'bg-grey-1 text-default-text')
          .with(CheckStatus.Closed, () => 'bg-default-light-green text-default-dark-green')
          .with(CheckStatus.Void, () => 'bg-default-light-violet text-default-dark-violet')
          .exhaustive();

        const statusText = match(status)
          .with(CheckStatus.Open, () => 'Open')
          .with(CheckStatus.Closed, () => 'Closed')
          .with(CheckStatus.Void, () => 'Void')
          .exhaustive();

        return (
          <Badge size="sm" className={badgeClasses}>
            {statusText}
          </Badge>
        );
      },
      enableGrouping: true,
      enableColumnFilter: true,
      getGroupingValue: (row) => row.status || 'None',
      meta: {
        filterType: 'multi-select',
        disableNullFilter: true,
        getFilterOptionsFn: async () => {
          return Object.values(CheckStatus).map((status) => ({ label: status.charAt(0).toUpperCase() + status.slice(1), value: status }));
        },
      },
    }),
    // 3. Schedule Check (with trigger summary, e.g. "B - 500 Kms or 6 m")
    columnHelper.accessor((row) => formatScheduleCheckLabel(row), {
      id: 'scheduleCheck',
      header: 'Schedule Check',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: false,
      enableColumnFilter: false,
      enableSorting: false,
    }),
    // 4. Due Progress
    columnHelper.accessor('dueProgress', {
      header: 'Due Progress',
      cell: ({ getValue, row }) => {
        const value = getValue();
        const isUnlinked = row.original.isUnlinked;
        const allUnitProgress = row.original.allUnitProgress;
        const anchorType = row.original.schedule?.triggers?.[0]?.anchorType;

        // Show check mark once the check has been completed — closed, or left open by
        // a submission the workspace's auto-close setting keeps open for manual review
        if (isCheckCompleted(row.original)) {
          return (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <CheckIcon className="text-default-green h-5 w-5" />
                </TooltipTrigger>
                <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">
                  <TooltipArrow className="fill-default-text" />
                  Schedule Check is complete
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        if (isUnlinked) {
          const hasScheduleCycle = row.original.scheduleCycleId != null;
          const unlinkedTooltip =
            anchorType === AnchorType.Crawling && hasScheduleCycle
              ? 'Due progress appears after the first check is closed'
              : "Pick a schedule check to define this asset's starting position";
          return (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div className="flex w-full items-center">
                    <div className={`h-3 flex-1 rounded-l-full ${row.getIsSelected() ? 'bg-grey-3' : 'bg-grey-2'}`} />
                    <InformationIcon className="text-grey-5 mx-1 h-4 w-4 shrink-0" />
                    <div className={`h-3 flex-1 rounded-r-full ${row.getIsSelected() ? 'bg-grey-3' : 'bg-grey-2'}`} />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">
                  <TooltipArrow className="fill-default-text" />
                  {unlinkedTooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        if (value == null) return <span className="text-secondary-text">-</span>;

        const dateRemaining = row.original.dateRemaining;
        const allDateProgress = row.original.allDateProgress;
        const status = (() => {
          if (value >= 100 || (dateRemaining && dateRemaining.days <= 0)) return 'overdue';
          if (value <= 75) return 'ok';
          return 'due';
        })();

        const colors = match(status)
          .with('overdue', () => ({
            primary: 'rgb(var(--default-red))',
            secondary: 'rgb(var(--secondary-red))',
          }))
          .with('due', () => ({
            primary: 'rgb(var(--default-yellow))',
            secondary: 'rgb(var(--secondary-yellow))',
          }))
          .with('ok', () => ({
            primary: 'rgb(var(--default-green))',
            secondary: 'rgb(var(--secondary-green))',
          }))
          .otherwise(() => ({
            primary: 'rgb(var(--default-green))',
            secondary: 'rgb(var(--secondary-green))',
          }));

        const progressBar = (
          <div className="bg-grey-2 h-3 w-full overflow-hidden rounded-full">
            <div
              className="h-full rounded-full"
              style={{
                width: value < 0 ? 0 : `max(12px, ${Math.min(value, 100)}%)`,
                background: `repeating-linear-gradient(
                45deg,
                ${colors.primary},
                ${colors.primary} 8px,
                ${colors.secondary} 8px,
                ${colors.secondary} 16px
              )`,
              }}
            />
          </div>
        );

        const hasDateContent = allDateProgress && allDateProgress.length > 0;
        const hasUnitContent = allUnitProgress && allUnitProgress.length > 0;
        const hasTooltipContent = hasUnitContent || hasDateContent || dateRemaining;

        if (hasTooltipContent) {
          return (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>{progressBar}</TooltipTrigger>
                <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">
                  <TooltipArrow className="fill-default-text" />
                  <ul className="flex flex-col gap-1">
                    {allUnitProgress?.map((unit, index) => (
                      <li key={`unit-${index}`} className="flex items-center gap-2 text-xs">
                        <span>•</span>
                        <span>
                          {unit.remaining.toLocaleString()} {unit.unitAbbreviation} remaining{' '}
                          <span className="font-semibold">({unit.progress.toFixed(0)}%)</span>
                        </span>
                      </li>
                    ))}
                    {hasDateContent
                      ? allDateProgress.map((d, index) => (
                          <li key={`date-${index}`} className="flex items-center gap-2 text-xs">
                            <span>•</span>
                            <span>
                              {`${d.days} days remaining`} <span className="font-semibold">({d.progress.toFixed(0)}%)</span>
                            </span>
                          </li>
                        ))
                      : dateRemaining && (
                          <li className="flex items-center gap-2 text-xs">
                            <span>•</span>
                            <span>{`${dateRemaining.days} days remaining`}</span>
                          </li>
                        )}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        return progressBar;
      },
      meta: {
        cellClassName: 'min-w-[180px]',
      },
      enableSorting: true,
      enableGrouping: false,
      enableColumnFilter: false,
    }),
    // 5. Unit Remaining (shows only the winning trigger's unit remaining)
    columnHelper.accessor('unitRemaining', {
      id: 'unitRemaining',
      header: 'Unit Remaining',
      cell: ({ getValue, row }) => {
        if (isCheckCompleted(row.original)) {
          return <span className="text-secondary-text">-</span>;
        }
        const single = getValue();
        if (!single) return <span className="text-secondary-text">-</span>;
        return (
          <span>
            {single.value.toLocaleString()} {single.unitAbbreviation}
          </span>
        );
      },
      enableSorting: false,
      enableGrouping: false,
      enableColumnFilter: false,
    }),
    // 6. Date Remaining (shows only the closest/winning trigger)
    columnHelper.accessor('dateRemaining', {
      id: 'dateRemaining',
      header: 'Date Remaining',
      cell: ({ getValue, row }) => {
        if (isCheckCompleted(row.original)) {
          return <span className="text-secondary-text">-</span>;
        }
        const dateRemaining = getValue();
        if (!dateRemaining) return <span className="text-secondary-text">-</span>;
        return <span>{`${dateRemaining.days} days`}</span>;
      },
      enableSorting: false,
      enableGrouping: false,
      enableColumnFilter: false,
    }),
    // 7. Schedule
    columnHelper.accessor((row) => row.schedule?.name, {
      id: 'schedule',
      header: 'Schedule',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: true,
      enableColumnFilter: true,
      getGroupingValue: (row) => row.schedule?.name || 'None',
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { search: inputValue } : undefined;
          const { data } = await (await ApiService.GetSchedules({ limit: 100, filters: { ...filters, state: RecordState.Active } })).json();

          const resultsMap = new Map(data.map((schedule) => [schedule.id, { label: schedule.name, value: schedule.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingSchedules } = await (
                await ApiService.GetSchedules({
                  limit: 100,
                  filters: { fields: { id: missingIds }, state: RecordState.Active },
                })
              ).json();

              for (const schedule of missingSchedules) {
                resultsMap.set(schedule.id, { label: schedule.name, value: schedule.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    // 8. Completed Date
    columnHelper.accessor((row) => (row.completedAt ? formatDateWithUserTimezone(new Date(row.completedAt), userTimezone) : null), {
      id: 'completedDate',
      header: 'Completed Date',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableSorting: true,
      enableGrouping: false,
      meta: {
        filterType: 'date-range',
      },
    }),
    // 9. Completed By
    columnHelper.accessor((row) => row.completedByUser, {
      id: 'completedBy',
      header: 'Completed By',
      cell: ({ row }) => {
        const check = row.original;
        const completedBy = check.completedByUser;
        const fullName = EntityRefUtil.createUserFullNameRef(completedBy);
        const value = fullName || '-';
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(completedBy?.firstName || '');
        const completedByFullInitials = initials(fullName);
        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={completedByFullInitials}
            avatarUrl=""
            avatarBackgroundColorClass={avatarBackgroundColorClass}
          />
        );
      },
      getGroupingValue: (row) => {
        return EntityRefUtil.createUserFullNameRef(row.completedByUser, 'None');
      },
      enableGrouping: true,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data: userData } = await (await ApiService.GetUsers({ limit: 100, ...filters })).json();
          const { data: sitemateUserData } = await (
            await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })
          ).json();

          const resultsMap = new Map();
          userData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });
          sitemateUserData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingUsers } = await (await ApiService.GetUsers({ limit: 100, filters: { id: missingIds } })).json();
              const { data: missingSitemateUsers } = await (
                await ApiService.GetUsers({ limit: 100, filters: { id: missingIds, userType: 'sitemate-users' } })
              ).json();

              for (const user of missingUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
              for (const user of missingSitemateUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    // 10. Schedule Check Tags
    createTagsColumn(columnHelper, { header: 'Schedule Check Tags', entity: TagEntity.Check, variant: 'violet' }),
    // 11. Dynamic per-unit columns: completed reading + last reading from summary
    ...(opts?.units
      ? opts.units.flatMap((unit) => [
          columnHelper.accessor((row) => row.completedReadings?.find((r) => r.unitName === unit.name)?.reading ?? null, {
            id: `completedReading-${unit.id}`,
            header: `Completed ${unit.abbreviation}`,
            cell: ({ getValue }) => {
              const value = getValue();
              if (value == null) return <span className="text-secondary-text">-</span>;
              return (
                <span>
                  {value} {unit.abbreviation}
                </span>
              );
            },
            enableSorting: false,
            enableGrouping: false,
            enableColumnFilter: false,
            meta: { columnGroup: 'Schedule Check Record' },
          }),
          columnHelper.accessor((row) => row.summary?.find((s) => s.unit === unit.name)?.currentAssetReading ?? null, {
            id: `lastReading-${unit.id}`,
            header: `Last Reading ${unit.abbreviation}`,
            cell: ({ getValue }) => {
              const value = getValue();
              return value != null ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
            },
            enableSorting: false,
            enableGrouping: false,
            enableColumnFilter: false,
            meta: { columnGroup: ASSET_SECTION_LABEL },
          }),
        ])
      : []),
  ] as AccessorKeyColumnDef<GetChecksResponseDTO['data'][number]>[];
};

export const groupOptions = [
  { label: 'None', accessor: '' },
  ...createAssetScheduleCheckColumns()
    .filter((column) => {
      const availableGroupColumns = ['assetCategory', 'schedule', 'status', 'completedBy'];
      return !column.accessorKey && column.id ? availableGroupColumns.includes(column.id) : availableGroupColumns.includes(column.accessorKey);
    })
    .map((column) => ({ label: column.header?.toString() || '', accessor: !column.accessorKey && column.id ? column.id : column.accessorKey })),
] satisfies { label: string; accessor: string }[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<ReturnType<typeof createAssetScheduleCheckColumns>[number]['id'] | ReturnType<typeof createAssetScheduleCheckColumns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetChecksRequestDTO['filters']>['fields']>
> = {
  schedule: 'scheduleIds',
  status: 'status',
  completedDate: 'completedDate',
  completedBy: 'completedById',
  tags: 'tagIds',
};
